import {
  AppCard,
  Button,
  Form,
  FormAsyncAutocomplete,
  FormTextField,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiContentSaveOutline, mdiExclamation } from "@mdi/js";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { Material } from "../../../../@types/services/Material";
import type { PlantField } from "../../../../@types/services/PlantField";
import type { RequesterArea } from "../../../../@types/services/RequesterArea";
import type { User } from "../../../../@types/services/User";
import type { ValorationRequestType } from "../../../../@types/services/ValorationRequest";
import { listPlants } from "../../../../__generated__/orval/endpoints/plants/plants";
import type { EntitiesPlantListResponse } from "../../../../__generated__/orval/models/entitiesPlantListResponse.zod";
import type { ListPlantsParams } from "../../../../__generated__/orval/models/listPlantsParams.zod";
import { FileUpload } from "../../../../components/molecules/FileUpload";
import { MaterialNumberField } from "../../../../components/molecules/MaterialNumberField";
import { searchRequestId } from "../../../../services/mmRequests";
import { fetchPlantFields } from "../../../../services/plantFields";
import { fetchRequesterAreas } from "../../../../services/requesterAreas";
import { fetchUsers } from "../../../../services/users";
import { createValorationRequest } from "../../../../services/valorationRequests";

type FormValues = {
  mm_request_attributes: {
    id: number;
    matnumber: Material[];
    subject: string;
    description: string;
    requester_field: PlantField | null;
    requester_area: RequesterArea | null;
    file_attachments?: string[];
    reason?: string;
    backup_requester_id?: User | null;
    previous_ticket_id?: { id: string } | null;
  };
  plant_ids: EntitiesPlantListResponse["results"];
};

const ValorationRequest: React.FC = () => {
  const { t } = useTranslation();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmStep, setConfirmStep] = useState(1);
  const [formData, setFormData] = useState<FormValues | null>(null);

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      mm_request_attributes: {
        backup_requester_id: null,
        description: "",
        file_attachments: [],
        matnumber: [],
        previous_ticket_id: null,
        requester_area: null,
        requester_field: null,
        subject: "",
      },
      plant_ids: [],
    },
  });

  const requesterField = watch("mm_request_attributes.requester_field");

  const handleClose = useCallback(() => {
    setOpenConfirmDialog(false);
    setConfirmStep(1);
  }, []);

  const handleNext = useCallback(() => {
    setConfirmStep(2);
  }, []);

  const handleBack = useCallback(() => {
    setConfirmStep(1);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!formData) return;

    handleClose();
    setLoadingSubmit(true);

    const formattedData: ValorationRequestType = {
      mm_request_attributes: {
        ...formData.mm_request_attributes,
        backup_requester_id:
          formData.mm_request_attributes.backup_requester_id?.id || undefined,
        matnumber: formData.mm_request_attributes.matnumber.map((material) =>
          typeof material === "string"
            ? material
            : (material as Material).matnumber,
        ),
        previous_ticket_id:
          formData.mm_request_attributes.previous_ticket_id?.id || undefined,
        requester_area:
          formData.mm_request_attributes.requester_area?.name || "",
        requester_field:
          formData.mm_request_attributes.requester_field?.name || "",
      },
      plant_ids: formData.plant_ids.map((plant) => plant.id.toString()) || [],
    };

    await createValorationRequest(formattedData)
      .then((response) => {
        enqueueSnackbar(t("valorationRequests.successMessage"), {
          variant: "success",
        });
        window.location.href = `/vbuyer/mm_requests?flash_message=${t("valorationRequests.flashMessage")}${response.mm_request.id}`;
      })
      .catch(() => {
        setLoadingSubmit(false);
        enqueueSnackbar(t("valorationRequests.errorMessage"), {
          variant: "error",
        });
      });
  }, [formData, handleClose, t]);

  const onSubmit: SubmitHandler<FormValues> = useCallback(async (data) => {
    setFormData(data);
    setOpenConfirmDialog(true);
  }, []);

  const handleFilesChange = useCallback(
    (fileAttachments: string[]) => {
      setValue("mm_request_attributes.file_attachments", fileAttachments);
    },
    [setValue],
  );

  return (
    <PageContainer
      backTo="/vbuyer/mm_requests/create_new_requests"
      maxWidth="md"
      showBackButton
      title={t("common.valorationRequest")}
    >
      <AppCard>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <FormAsyncAutocomplete
              control={control}
              fetchOptions={fetchUsers}
              filters={(searchTerm) => ({
                equinor_user: true,
                name_or_login: searchTerm,
              })}
              getOptionLabel={(option) => option.full_name_with_login}
              label={t("valorationRequests.backupRequester")}
              minSearchLength={2}
              name="mm_request_attributes.backup_requester_id"
              queryKey={["backupRequesters", "infinite"]}
            />

            <FormAsyncAutocomplete
              control={control}
              // TODO: Create V3 API service
              fetchOptions={async (params?: { id?: string }) => {
                const ids = await searchRequestId(params?.id ?? "");
                const results = ids.map((id) => ({ id }));
                return {
                  pagination: {
                    current_page: 1,
                    items_per_page: results.length,
                    total_pages: 1,
                  },
                  results,
                  total_items: results.length,
                };
              }}
              filters={(searchTerm) => ({
                id: searchTerm,
              })}
              getOptionLabel={(option) => option.id}
              label={t("valorationRequests.previousTicket")}
              minSearchLength={2}
              name="mm_request_attributes.previous_ticket_id"
              queryKey={["previousTickets", "infinite"]}
            />

            <FormAsyncAutocomplete
              control={control}
              fetchOptions={fetchPlantFields}
              filters={(searchTerm) => ({
                active_on_mm_request: true,
                enabled: true,
                name: searchTerm,
              })}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) =>
                typeof value !== "string" && option.id === value.id
              }
              label={t("valorationRequests.requesterField")}
              name="mm_request_attributes.requester_field"
              queryKey={["requesterFields", "infinite"]}
              required
              rules={{
                required: t("valorationRequests.requesterFieldRequiredMessage"),
              }}
            />

            <FormAsyncAutocomplete
              control={control}
              disabled={!requesterField}
              fetchOptions={fetchRequesterAreas}
              filters={(searchTerm) => ({
                enabled: true,
                name: searchTerm,
                plant_field_name: requesterField ? requesterField.name : "",
              })}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) =>
                typeof value !== "string" && option.id === value.id
              }
              label={t("valorationRequests.requesterArea")}
              name="mm_request_attributes.requester_area"
              queryKey={["requesterAreas", "infinite"]}
            />

            <FormAsyncAutocomplete
              control={control}
              fetchOptions={listPlants}
              filters={(searchTerm): ListPlantsParams => ({
                active_on_mm_request: true,
                enabled: true,
                plant_number_with_name: searchTerm,
              })}
              getOptionLabel={(option) => option.plant_number_with_name}
              label={t("valorationRequests.plants")}
              minSearchLength={2}
              multiple
              name="plant_ids"
              queryKey={["plants", "infinite"]}
            />

            <MaterialNumberField
              control={control}
              helperText={t("valorationRequests.materialHelper")}
              label={t("valorationRequests.material")}
              name="mm_request_attributes.matnumber"
              rules={{
                required: t("valorationRequests.requiredMessage"),
              }}
              setValue={setValue}
            />

            <FormTextField
              control={control}
              helperText={t("mmRequests.subjectHelper")}
              label={t("valorationRequests.subject")}
              name="mm_request_attributes.subject"
            />

            <FormTextField
              control={control}
              helperText={t("mmRequests.descriptionHelper")}
              label={t("valorationRequests.description")}
              multiline
              name="mm_request_attributes.description"
              rows={6}
            />

            <Divider />
            <Alert severity="info">
              {t("valorationRequests.materialEvidenceRequiredAlert")}
            </Alert>
            <Controller
              control={control}
              name="mm_request_attributes.file_attachments"
              render={() => (
                <FormControl
                  error={!!errors.mm_request_attributes?.file_attachments}
                  variant="outlined"
                >
                  <FileUpload
                    allowMultipleFiles
                    onFilesChange={handleFilesChange}
                  />
                  {!!errors.mm_request_attributes?.file_attachments && (
                    <FormHelperText>
                      {errors.mm_request_attributes.file_attachments?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
              rules={{
                required: t("valorationRequests.requiredMessage"),
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                color="success"
                loading={loadingSubmit}
                startIcon={<Icon icon={mdiContentSaveOutline} />}
                type="submit"
              >
                {t("common.save")}
              </Button>
            </Box>
          </Box>
        </Form>
      </AppCard>

      <Dialog
        aria-describedby="alert-dialog-description"
        aria-labelledby="alert-dialog-title"
        onClose={handleClose}
        open={openConfirmDialog}
      >
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Icon
            fontSize="large"
            icon={mdiExclamation}
            sx={{ color: "#ED6C02" }}
          />
          <DialogTitle>
            {t("valorationRequests.dialog.title", {
              step: confirmStep,
            })}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirmStep === 1
                ? t("valorationRequests.dialog.step1.text")
                : t("valorationRequests.dialog.step2.text")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={confirmStep === 1 ? handleClose : handleBack}>
              {confirmStep === 1
                ? t("valorationRequests.dialog.step1.cancelButton")
                : t("valorationRequests.dialog.step2.backButton")}
            </Button>
            <Button onClick={confirmStep === 1 ? handleNext : handleConfirm}>
              {confirmStep === 1
                ? t("valorationRequests.dialog.step1.nextButton")
                : t("valorationRequests.dialog.step2.confirmButton")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {!!loadingSubmit && (
        <Dialog open>
          <DialogContent sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress />
            <DialogContentText sx={{ mt: 2 }}>
              {t("valorationRequests.dialog.processing")}
            </DialogContentText>
          </DialogContent>
        </Dialog>
      )}
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/valoration_requests/new",
)({
  component: ValorationRequest,
});
